import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../api/client';

const AuthContext = createContext(null);

// Auto sign-out after 20 minutes without user activity (security requirement).
const INACTIVITY_LIMIT_MS = 20 * 60 * 1000;
// How often the idle check runs.
const ACTIVITY_CHECK_MS = 60 * 1000;
// Tokens live 25 min server-side; renew every 10 min while the user is active.
const TOKEN_REFRESH_MS = 10 * 60 * 1000;
const ACTIVITY_KEY = 'lastActivityAt';
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const lastActivityRef = useRef(Date.now());
    const lastRefreshRef = useRef(Date.now());
    const userRef = useRef(null);
    userRef.current = user;

    const clearSession = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem(ACTIVITY_KEY);
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
    }, []);

    const refreshToken = useCallback(async () => {
        try {
            const { data } = await api.post('/auth/refresh', {});
            if (data?.token) {
                localStorage.setItem('token', data.token);
                api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
            }
            lastRefreshRef.current = Date.now();
        } catch (error) {
            // 401 is handled by the global auth:unauthorized listener
        }
    }, []);

    useEffect(() => {
        // Check if user is logged in
        const checkAuth = async () => {
            try {
                // If we stored token in localStorage manually (as returned by login)
                const token = localStorage.getItem('token');
                if (token) {
                    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    const { data } = await api.get('/auth/me');
                    setUser(data);
                }
            } catch (error) {
                console.error('Auth check failed', error);
                localStorage.removeItem('token');
            } finally {
                setLoading(false);
            }
        };
        checkAuth();
    }, []);

    // The api client dispatches this when any request comes back 401
    // (expired/invalid token) — drop the session so ProtectedRoute redirects.
    useEffect(() => {
        const onUnauthorized = () => {
            if (userRef.current) {
                clearSession();
                toast.info('Your session expired. Please sign in again.');
            }
        };
        window.addEventListener('auth:unauthorized', onUnauthorized);
        return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
    }, [clearSession]);

    // Inactivity tracking + sliding token renewal, only while signed in.
    useEffect(() => {
        if (!user) return;

        const markActivity = () => {
            const now = Date.now();
            lastActivityRef.current = now;
            // Shared across tabs; throttle writes to one per 5s.
            if (now - Number(localStorage.getItem(ACTIVITY_KEY) || 0) > 5000) {
                localStorage.setItem(ACTIVITY_KEY, String(now));
            }
        };
        markActivity();
        // Renew immediately: after a page reload the stored token may be close
        // to its 25-min expiry while the 10-min renewal timer restarts from zero.
        refreshToken();
        ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, markActivity, { passive: true }));

        const timer = setInterval(async () => {
            const lastActivity = Math.max(
                lastActivityRef.current,
                Number(localStorage.getItem(ACTIVITY_KEY) || 0)
            );
            const idleFor = Date.now() - lastActivity;

            if (idleFor >= INACTIVITY_LIMIT_MS) {
                api.post('/auth/logout', {}).catch(() => { });
                clearSession();
                toast.info('You were signed out after 20 minutes of inactivity.');
                return;
            }

            // Renew the short-lived token only when the user was recently active,
            // so an idle session also expires on the server.
            const recentlyActive = idleFor < 2 * ACTIVITY_CHECK_MS;
            if (recentlyActive && Date.now() - lastRefreshRef.current >= TOKEN_REFRESH_MS) {
                await refreshToken();
            }
        }, ACTIVITY_CHECK_MS);

        return () => {
            ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, markActivity));
            clearInterval(timer);
        };
    }, [user, clearSession, refreshToken]);

    const login = async (email, password) => {
        const { data } = await api.post('/auth/login', { email, password });
        localStorage.setItem('token', data.token);
        localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
        api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        setUser(data.user);
        return data;
    };

    const logout = () => {
        // Also clear the httpOnly cookie server-side (JS can't remove it).
        api.post('/auth/logout', {}).catch(() => { });
        clearSession();
    };

    return (
        <AuthContext.Provider value={{ user, login, logout, loading }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
